import styled from 'styled-components'
import { Text, Flex, Box, tokens } from '../../index'
import plusIcon from 'src/assets/v2/img/plus_icon.svg'
import { TeamMember } from './types'
import './style.scss'

export interface TeammatesMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}

interface Props {
    members: TeamMember[]
    roleLabels: Record<string, string>
    onAdd?: () => void
    onResend?: (member: TeamMember) => void
    resendingId?: string | number | null
    meta?: TeammatesMeta | null
    onPageChange?: (page: number) => void
    pageLoading?: boolean
}

/* ── Status / resend cell ────────────────────────────────────────────────── */

const orange = {
    text: '#B54708',
    border: '#FEC84B',
    bg: '#FFFAEB',
    bgHover: '#FEF0C7',
}

// Same neutral look for every status (Invited, Active, disabled, etc.)
const StatusText = styled(Text)`
    color: ${tokens.textMuted};
    font-size: 1.3rem;
    font-weight: 500;
`

const HoverableStatusCell = styled.div`
    position: relative;
    min-width: 12rem;
    height: 3.2rem;
    display: flex;
    align-items: center;
    justify-content: flex-end;
`

const StatusLayer = styled.div<{ hideOnHover?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    transition: opacity 0.15s ease;

    ${HoverableStatusCell}:hover & {
        opacity: ${({ hideOnHover }) => (hideOnHover ? 0 : 1)};
        pointer-events: ${({ hideOnHover }) => (hideOnHover ? 'none' : 'auto')};
    }
`

const ResendLayer = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;

    ${HoverableStatusCell}:hover & {
        opacity: 1;
        pointer-events: auto;
    }
`

const ResendButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 12rem;
    padding: 0.7rem 1.4rem;
    border-radius: 0.8rem;
    border: 1px solid ${orange.border};
    background-color: ${orange.bg};
    color: ${orange.text};
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background-color: ${orange.bgHover};
        border-color: ${orange.text};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`

/* ── Pagination (unchanged) ──────────────────────────────────────────────── */

const PageButton = styled.button<{ active?: boolean }>`
    min-width: 3.2rem;
    height: 3.2rem;
    padding: 0 0.8rem;
    border-radius: 0.8rem;
    border: 1px solid ${({ active }) => (active ? tokens.primary : tokens.border)};
    background-color: ${({ active }) => (active ? tokens.primarySoft : tokens.white)};
    color: ${({ active }) => (active ? tokens.primaryDark : tokens.textDark)};
    font-size: 1.4rem;
    font-weight: ${({ active }) => (active ? 700 : 500)};
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        border-color: ${tokens.primary};
        background-color: ${tokens.primarySoft};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
`

const NavButton = styled(PageButton)`
    font-size: 1.6rem;
    color: ${tokens.textMuted};
`

function getPageWindow(current: number, total: number): (number | 'ellipsis')[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1)
    }

    const pages = new Set<number>([1, total, current - 1, current, current + 1])
    const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)

    const withEllipsis: (number | 'ellipsis')[] = []
    sorted.forEach((page, i) => {
        if (i > 0 && page - sorted[i - 1] > 1) withEllipsis.push('ellipsis')
        withEllipsis.push(page)
    })
    return withEllipsis
}

const Pagination = ({
                        meta,
                        onPageChange,
                        pageLoading,
                    }: {
    meta: TeammatesMeta
    onPageChange?: (page: number) => void
    pageLoading?: boolean
}) => {
    const { currentPageNumber, pageCount, total, perPage } = meta

    if (pageCount <= 1) return null

    const start = (currentPageNumber - 1) * perPage + 1
    const end = Math.min(currentPageNumber * perPage, total)

    return (
        <Flex justify="space-between" items="center" topMargin="2" wrap="wrap" gap="1.2">
            <Text fontSize="1.3" color={tokens.textMuted}>
                Showing {start}–{end} of {total}
            </Text>

            <Flex gap="0.6" items="center">
                <NavButton
                    type="button"
                    disabled={currentPageNumber === 1 || pageLoading}
                    onClick={() => onPageChange?.(currentPageNumber - 1)}
                >
                    ‹
                </NavButton>

                {getPageWindow(currentPageNumber, pageCount).map((p, i) =>
                    p === 'ellipsis' ? (
                        <Text key={`ellipsis-${i}`} fontSize="1.4" color={tokens.textMuted}>
                            …
                        </Text>
                    ) : (
                        <PageButton
                            key={p}
                            type="button"
                            active={p === currentPageNumber}
                            disabled={pageLoading}
                            onClick={() => onPageChange?.(p)}
                        >
                            {p}
                        </PageButton>
                    ),
                )}

                <NavButton
                    type="button"
                    disabled={currentPageNumber === pageCount || pageLoading}
                    onClick={() => onPageChange?.(currentPageNumber + 1)}
                >
                    ›
                </NavButton>
            </Flex>
        </Flex>
    )
}

/* ── Team list ────────────────────────────────────────────────────────────── */

const TeamList = ({
                      members,
                      roleLabels,
                      onAdd,
                      onResend,
                      resendingId,
                      meta,
                      onPageChange,
                      pageLoading,
                  }: Props) => {
    if (!members.length) return null

    return (
        <div className="team-list">
            <div className="team-list__header">
                <div className="team-list__title">
                    <Text fontSize="1.8" fontWeight="700">
                        Team Members
                    </Text>

                    <span className="team-list__line" />
                </div>

                <button
                    className="team-list__add"
                    onClick={onAdd}
                    type="button"
                >
                    <img src={plusIcon} alt="" />
                    <span>Add Team Member</span>
                </button>
            </div>

            <div className="team-list__body" style={pageLoading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                {members.map(member => {
                    const canResend = member.status === 'Invited' && !!onResend
                    const isSending = resendingId === member.id

                    return (
                        <div
                            className="team-list__row"
                            key={member.id}
                        >
                            <div className="team-list__cell">
                                <span>Name</span>
                                <strong>
                                    {member.firstName} {member.lastName}
                                </strong>
                            </div>

                            <div className="team-list__cell">
                                <span>User Name</span>
                                <strong>{member.userName}</strong>
                            </div>

                            <div className="team-list__cell">
                                <span>Email Address</span>
                                <strong>{member.email}</strong>
                            </div>

                            <div className="team-list__cell">
                                <span>Role</span>
                                <strong>
                                    {roleLabels[member.role]}
                                </strong>
                            </div>

                            <div className="team-list__status-cell">
                                {canResend ? (
                                    <HoverableStatusCell>
                                        <StatusLayer hideOnHover>
                                            <StatusText>{member.status}</StatusText>
                                        </StatusLayer>

                                        <ResendLayer>
                                            <ResendButton
                                                type="button"
                                                onClick={() => onResend!(member)}
                                                disabled={isSending}
                                            >
                                                {isSending ? 'Sending…' : 'Resend Invite'}
                                            </ResendButton>
                                        </ResendLayer>

                                        {/* while sending, keep the button visible without needing hover */}
                                        {isSending && (
                                            <ResendLayer style={{ opacity: 1, pointerEvents: 'auto' }}>
                                                <ResendButton type="button" disabled>
                                                    Sending…
                                                </ResendButton>
                                            </ResendLayer>
                                        )}
                                    </HoverableStatusCell>
                                ) : (
                                    <StatusText>{member.status}</StatusText>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            {meta && (
                <Box paddingX="0" topMargin="0">
                    <Pagination meta={meta} onPageChange={onPageChange} pageLoading={pageLoading} />
                </Box>
            )}
        </div>
    )
}

export default TeamList